import "./styles/Trends.css"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import {
  fetchStreamsDaily,
  fetchStreamsCountry,
  fetchStreamsDemographic
} from "../../../actions/trendsActions"
import { Artist, Track, TrendFilter, TrendsProps } from "../../../types"
import {
  KDropdown,
  KSelectRangeDate,
  KSpan
} from "kahuna-base-react-components"
import { getCountryCodeMap, lang } from "../../../constants/languages"
import Loading from "../../ui/Loading"
//@ts-ignore
import EarthIcon from "../../../assets/country-icons/earth.svg"
import StreamsDaily from "./streams-daily/StreamsDaily"
import StreamsCountry from "./streams-country/StreamsCountry"
import StreamsDemographic from "./streams-demographic/StreamsDemographic"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { toast } from "react-toastify"
import {
  ANALYSIS_TAB_OPTIONS,
  countryIcons,
  defaultEndDate,
  defaultStartDate,
  errorFilledToastOptions,
  TREND_TRACK_EVENT_TYPE,
  trendsIcons
} from "../../../constants"
import { capitalize } from "../../../utility"
// @ts-ignore
import SpotifyLogo from "../../../assets/release-platform-icons/SpotifyLogo.png"
import { countryMapEn } from "../../../constants/languages/countryMap/en"
import { eventTypeOptions } from "../analysis-new/artist-overview/ArtistOverview"
import { socialTypeOptions } from "../analysis-new/audience-and-discovery/AudienceAndDiscovery"

const MILISECONDS_MONTH = 2678400000

enum TabOptions {
  STREAMS_DAILY,
  STREAMS_COUNTRY,
  STREAMS_DEMOGRAPHIC
}

export enum ChartIndex {
  OVERVIEW_BAR_CHART_TRACK = 1,
  OVERVIEW_PIE_CHART_PLATFORM = 2,
  OVERVIEW_BAR_CHART_PLATFORM = 3,
  OVERVIEW_LINE_CHART_TRACK = 4,
  OVERVIEW_LINE_CHART_PLATFORM = 5,
  COUNTRY_PIE_CHART = 6,
  COUNTRY_LINE_CHART = 7,
  COUNTRY_BAR_CHART = 8,
  DEMOGRAPHIC_PIE_CHART_AGE = 9,
  DEMOGRAPHIC_LINE_CHART_AGE = 10,
  DEMOGRAPHIC_PIE_CHART_SEX = 11,
  DEMOGRAPHIC_LINE_CHART_SEX = 12
}

// It is needed to be sure a platform option's value is always same.
export const platformValueMap = {
  apple: 1,
  audiomack: 2,
  boomplay: 3,
  deezer: 4,
  facebook: 5,
  instagram: 6,
  itunes: 7,
  shazam: 8, 
  snap: 9,
  soundcloud: 10,
  spotify: 11
}

export const EnlargeIcon = ({ setIndex, chartIndex }: { setIndex: (index: number) => void; chartIndex: number }) => {
  return (
    <div
      onClick={() => {
        setIndex(chartIndex)
      }}
      className="flex items-center cursor-pointer justify-center p-1 rounded-[4px] hover:bg-[#F7F7F7] absolute right-2 top-2"
    >
      <img src={"/enlarge.svg"} className="w-4 h-4" />
    </div>
  )
}

const getDateRangeList = (startDate:Date, endDate:Date) => {
  const result = []

  const start = new Date(startDate)
  const end = new Date(endDate)

  const current = new Date(start)

  while (current <= end) {
    const y = current.getFullYear()
    const m = String(current.getMonth() + 1).padStart(2, "0")
    const d = String(current.getDate()).padStart(2, "0")

    result.push(`${y}${m}${d}`)

    current.setDate(current.getDate() + 1)
  }

  return result
}

const Trends = ({
  streamsDaily,
  fetchStreamsDaily,
  streamsCountry,
  fetchStreamsCountry,
  streamsDemographic,
  fetchStreamsDemographic,
  options,
  isAdmin,
  user,
  externalTab,
  commonDateValues,
  selectedUsers
}: TrendsProps) => {
  const [selectedTab, setSelectedTab] = useState<TabOptions | undefined>(undefined)
  const [filter, setFilter] = useState<TrendFilter>({
    dateValues: commonDateValues ?? [defaultStartDate, defaultEndDate],
    isrcList: [],
    countryList: [],
    ageGroupList: [],
    sexList: [],
    platformList: [],
    artistList: [],
    eventType: TREND_TRACK_EVENT_TYPE.DSP,
    isAdmin
  })
  const [platformOptions, setPlatformOptions] = useState<KSelectOption[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<KSelectOption[]>([])
  const [songOptions, setSongOptions] = useState<KSelectOption[]>([])
  const [artistOptions, setArtistOptions] = useState<KSelectOption[]>([])
  const [countryOptions, setCountryOptions] = useState<KSelectOption[]>([])
  const [sexOptions, setSexOptions] = useState<KSelectOption[]>([])
  const [ageGroupOptions, setAgeGroupOptions] = useState<KSelectOption[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchOptions, setFetchOptions] = useState(true)
  const [fullScreenChartIndex, setFullScreenChartIndex] = useState<number | undefined>(undefined)
  const [selectedSocialType, setSelectedSocialType] = useState<string>("social_consumption")
  const [normalizedEvent, setNormalizedEvent] = useState<
    "dsp" | "social_consumption" | "social_production" | "download"
  >("dsp")
  useEffect(() => {
    let event_type = "dsp"
    if (filter.eventType === "social") event_type = selectedSocialType
    else if (filter.eventType === "download") event_type = "download"
    setNormalizedEvent(event_type as typeof normalizedEvent)
  }, [filter.eventType, selectedSocialType])

  const dateList = useMemo(() => {
    const dates = filter.dateValues as Date[]
    if (!(dates?.[0] && dates?.[1])) return []

    return getDateRangeList(dates[0], dates[1])
  }, [filter.dateValues])

  const useTimeout = (timeoutInMs: number) => {
    setTimeout(() => {
      setLoading(false)
    }, timeoutInMs)
  }

  useEffect(() => {
    if (selectedTab === undefined) return
    setLoading(true)
    if (selectedTab === TabOptions.STREAMS_DAILY) {
      fetchStreamsDaily(filter, fetchOptions).then(() => useTimeout(200))
    } else if (selectedTab === TabOptions.STREAMS_COUNTRY) {
      fetchStreamsCountry(filter, fetchOptions).then(() => useTimeout(200))
    } else if (selectedTab === TabOptions.STREAMS_DEMOGRAPHIC) {
      fetchStreamsDemographic(filter, fetchOptions).then(() => useTimeout(200))
    } else {
      setLoading(false)
    }
    setFetchOptions(false)
  }, [filter])

  const processPlatformOptions = (platformOptions: string[]) => {
    return platformOptions.map((platform: string, index: number) => {
      return {
        label: capitalize(platform),
        value: platformValueMap?.[platform as keyof typeof platformValueMap] || (index + 20),
        value2: platform,
        icon: trendsIcons[platform as keyof typeof platformValueMap],
        iconLabel: platform === "spotify" && SpotifyLogo
      }
    })
  }

  useEffect(() => {
    if (!selectedUsers || !isAdmin) return
    const userIds = selectedUsers.map((selectedUser) => selectedUser.value)
    setTimeout(() => {
      setFetchOptions(true)
      setFilter((prevFilter) => ({ ...prevFilter, selectedUsers: userIds }))
    }, 100)
  }, [selectedUsers])

  useEffect(() => {
    if (!options) {
      return
    }

    const songOptions = options.songOptions || []
    const platformOptions = options.platformOptions || []
    const countryOptions = options.countryOptions || []
    const artistOptions = options.artistOptions || []
    const sexOptions = options.sexOptions || []
    const ageGroupOptions = options.ageGroupOptions || []

    setSongOptions(
      songOptions.map((track: Track, index: number) => {
        return { label: track.title, value: index, value2: track.isrc }
      })
    )

    const platformOptionsProcessed = processPlatformOptions(platformOptions)

    if (filter.platformList.length === 0) {
      // selectedPlatforms used to show all platforms selected when no filter.platformList applied, 
      // it is canceled in new logic. selectedPlatforms state might be redundant, but not deleted for now.
      //setSelectedPlatforms(platformOptionsProcessed)
      setSelectedPlatforms([])
    }

    setPlatformOptions(platformOptionsProcessed)

    setArtistOptions(
      artistOptions.map((artist: Artist, index: number) => {
        return { label: artist.name, value: artist.id || index, value2: artist.id?.toString() }
      })
    )

    const countryCodeMap = getCountryCodeMap()
    setCountryOptions(
      countryOptions.map((country: string, index: number) => {
        const label = countryCodeMap[country] ? `${countryCodeMap[country]} (${country})` : country
        const countryMap = countryMapEn
        const countryName = countryMap[country]
        const icon = countryIcons[countryName]
        return { label, value: index, value2: country, icon: icon ? icon : EarthIcon }
      })
    )

    setSexOptions(
      sexOptions.map((sex: string, index: number) => {
        return { label: sex, value: index, value2: sex }
      })
    )

    setAgeGroupOptions(
      ageGroupOptions.map((ageGroup: string, index: number) => {
        return { label: ageGroup, value: index, value2: ageGroup }
      })
    )
  }, [options])

  const findSelectedValue = (_key: string, options: KSelectOption[]) => {
    return options.filter((option) => filter[_key].includes(option.value2))
  }

  const multipleDropdownComponent = (
    title: string,
    options: KSelectOption[],
    _key: string,
    showOnlyIconsInMulti: boolean,
    width: number,
    sortSelectedFirst: boolean,
    reFetchOptions: boolean,
    dataTestId?: string
  ) => {
    return (
      <div>
        <div className="mb-1">
          <KSpan text={title} />
        </div>
        <div data-testid={dataTestId}>
          <KDropdown
            isClearable={true}
            isMulti={true}
            showOnlyIconsInMulti={showOnlyIconsInMulti}
            width={width}
            closeMenuOnSelect={false}
            options={options}
            onSelect={() => {}}
            onBlur={(selected) => {
              if (reFetchOptions) {
                setFetchOptions(true)
              }
              const value2List = (selected as KSelectOption[])?.map((entity) => entity.value2 || "") || []
              setFilter((prevFilter) => {
                if (value2List?.toString() === prevFilter[_key]?.toString()) {
                  return prevFilter
                }
                return { ...prevFilter, [_key]: value2List }
              })
              if (_key === "platformList") {
                setSelectedPlatforms(selected as KSelectOption[])
              }
            }}
            placeholder={lang.trends.filter.all}
            selected={_key === "platformList" ? selectedPlatforms : findSelectedValue(_key, options)}
            sortSelectedFirst={sortSelectedFirst}
          />
        </div>
      </div>
    )
  }

  const stringfyDateValue = (date?: Date) => {
    if (!date) {
      return
    }

    const month = lang.common.months_short[date.getMonth().toString()]
    return `${date.getDate()} ${month}`
  }

  const resetFilters = (eventType?: string) => {
    // all filters except selectedUsers and dateValues and eventType will be reset
    if (eventType) {
      setFilter((prev) => ({
        ...prev,
        ageGroupList: [],
        isrcList: [],
        artistList: [],
        sexList: [],
        countryList: [],
        platformList: [],
        eventType
      }))
      return
    }
    setFilter((prev) => ({
      ...prev,
      ageGroupList: [],
      isrcList: [],
      artistList: [],
      sexList: [],
      countryList: [],
      platformList: []
    }))
    setSelectedPlatforms([])
  }

  const renderFilters = () => {
    if (selectedTab === undefined) {
      return (
        <div>
          <KSpan text="Select to view" />
        </div>
      )
    }

    const platformSelection = multipleDropdownComponent(
      lang.trends.filter.platforms,
      platformOptions,
      "platformList",
      true,
      175,
      false,
      true,
      "platform-dropdown"
    )

    const artistSelection = multipleDropdownComponent(
      lang.trends.filter.artists,
      artistOptions,
      "artistList",
      false,
      250,
      false,
      true,
      "artist-dropdown"
    )
    const songSelection = multipleDropdownComponent(
      lang.trends.filter.songs,
      songOptions,
      "isrcList",
      false,
      250,
      false,
      false,
      "song-dropdown"
    )
    const countrySelection = multipleDropdownComponent(
      lang.trends.filter.countries,
      countryOptions,
      "countryList",
      true,
      250,
      true,
      false,
      "country-dropdown"
    )
    const sexSelection = multipleDropdownComponent(
      lang.trends.filter.sex,
      sexOptions,
      "sexList",
      false,
      250,
      false,
      false,
      "sex-dropdown"
    )
    const ageGroupSelection = multipleDropdownComponent(
      lang.trends.filter.age_group,
      ageGroupOptions,
      "ageGroupList",
      false,
      250,
      false,
      false,
      "agegroup-dropdown"
    )

    return (
      <div className="mb-10 flex justify-between">
        <div>
          <div className="flex gap-2 mb-4 flex-wrap">
            <div>
              <div className="mb-1">
                <KSpan text={lang.trends.filter.category} />
              </div>
              <KDropdown
                width={140}
                rightIcon="/analytics_icons/caret-down-new.svg"
                enableIndicator={true}
                options={eventTypeOptions}
                onSelect={(selected) => {
                  setFetchOptions(true)
                  setTimeout(() => {
                    resetFilters((selected as KSelectOption)?.value2 || TREND_TRACK_EVENT_TYPE.DSP)
                    setFullScreenChartIndex(undefined)
                  }, 100)
                }}
                selected={eventTypeOptions.find((option) => option.value2 === filter.eventType)}
              />
            </div>
            <div>{platformSelection}</div>
            <div>{artistSelection}</div>
            <div>{songSelection}</div>
          </div>
          <div>
            {selectedTab === TabOptions.STREAMS_COUNTRY && <div>{countrySelection}</div>}
            {selectedTab === TabOptions.STREAMS_DEMOGRAPHIC && (
              <div className="flex gap-2 flex-wrap">
                <div>{countrySelection}</div>
                <div>{sexSelection}</div>
                <div>{ageGroupSelection}</div>
              </div>
            )}
          </div>
          {filter.eventType === "social" && (
            <div>
              <div className="mb-1 mt-4">
                <KSpan text={lang.trends.filter.social_data_type} />
              </div>
              <div>
                <KDropdown
                  width={220}
                  rightIcon="/analytics_icons/caret-down-new.svg"
                  enableIndicator={true}
                  options={socialTypeOptions}
                  onSelect={(selected) => {
                    setSelectedSocialType((selected as KSelectOption)?.value2 || "")
                  }}
                  selected={socialTypeOptions.find((option) => option.value2 === selectedSocialType)}
                />
              </div>
            </div>
          )}
        </div>
        <div>
          {!commonDateValues && (
            <div className="flex flex-row gap-2 items-center mt-5">
              <div className="p-3.5 bg-[#F7F7F7] rounded-[10px] gap-1 h-12 flex items-center">
                <KSpan text={stringfyDateValue(filter.dateValues?.[0]) || ""} color="#000" fontWeight={500} />
                <KSpan text={`-`} color="#000" fontWeight={500} />
                <KSpan text={stringfyDateValue(filter.dateValues?.[1]) || ""} color="#000" fontWeight={500} />
              </div>
              <div data-testid="date-range-wrapper">
                <KSelectRangeDate
                  value={filter.dateValues}
                  onChange={(event) => {
                    if (!event) {
                      return
                    }
                    if (event[1] - event[0] > MILISECONDS_MONTH) {
                      toast.error(lang.trends.error_month_range_limit, errorFilledToastOptions)
                      return
                    }

                    setFetchOptions(true)
                    setTimeout(() => {
                      if (filter.dateValues !== event) {
                        setFilter((prevFilter) => ({ ...prevFilter, dateValues: event }))
                      }
                    }, 100)
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  const selectTab = (tab: number) => {
    // only for daily, country and demographic tabs
    if (selectedTab !== tab) setFetchOptions(true)
    setTimeout(() => {
      setSelectedTab(tab)
      resetFilters()
      setFullScreenChartIndex(undefined)
    }, 100)
  }

  useEffect(() => {
    if (!externalTab) return
    if (externalTab === ANALYSIS_TAB_OPTIONS.TRENDS_DAILY) selectTab(TabOptions.STREAMS_DAILY)
    else if (externalTab === ANALYSIS_TAB_OPTIONS.TRENDS_COUNTRY) selectTab(TabOptions.STREAMS_COUNTRY)
    else if (externalTab === ANALYSIS_TAB_OPTIONS.TRENDS_DEMOGRAPHIC) selectTab(TabOptions.STREAMS_DEMOGRAPHIC)
  }, [externalTab])

  const { start, end } = useMemo(() => {
    if (!Array.isArray(commonDateValues)) {
      return { start: undefined, end: undefined }
    }

    return {
      start: commonDateValues[0]?.getTime(),
      end: commonDateValues[1]?.getTime()
    }
  }, [commonDateValues])

  useEffect(() => {
    if (!Array.isArray(commonDateValues)) return

    setFilter((prev) => {
      if (!Array.isArray(prev.dateValues)) {
        return { ...prev, dateValues: commonDateValues }
      }

      const prevStart = prev.dateValues[0]?.getTime()
      const prevEnd = prev.dateValues[1]?.getTime()

      const nextStart = commonDateValues[0]?.getTime()
      const nextEnd = commonDateValues[1]?.getTime()

      if (prevStart === nextStart && prevEnd === nextEnd) {
        return prev
      }

      return {
        ...prev,
        dateValues: commonDateValues
      }
    })
  }, [start, end])

  return (
    <div className="flex bg-[#fff] h-screen">
      <div className="grow flex justify-center overflow-x-auto p-3 h-screen min-h-0">
        <div
          className="w-full flex flex-col items-center h-full"
          style={{
            paddingRight: 0,
            paddingLeft: 0 
          }}
        >
          <div className="pt-5 w-full flex-1 min-h-0 flex flex-col">
            {renderFilters()}
            {loading && <Loading />}
            {!loading && selectedTab === TabOptions.STREAMS_DAILY && (
              <StreamsDaily
                streamsDaily={streamsDaily}
                fullScreenChartIndex={fullScreenChartIndex}
                setFullScreenChartIndex={setFullScreenChartIndex}
                eventType={normalizedEvent}
                dateList={dateList}
              />
            )}
            {!loading && selectedTab === TabOptions.STREAMS_COUNTRY && (
              <StreamsCountry
                streamsCountry={streamsCountry}
                fullScreenChartIndex={fullScreenChartIndex}
                setFullScreenChartIndex={setFullScreenChartIndex}
                eventType={normalizedEvent}
                dateList={dateList}
              />
            )}
            {!loading && selectedTab === TabOptions.STREAMS_DEMOGRAPHIC && (
              <StreamsDemographic
                streamsDemographic={streamsDemographic}
                fullScreenChartIndex={fullScreenChartIndex}
                setFullScreenChartIndex={setFullScreenChartIndex}
                eventType={normalizedEvent}
                dateList={dateList}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    options: state.trends.options,
    streamsDaily: state.trends.streamsDaily,
    streamsCountry: state.trends.streamsCountry,
    streamsDemographic: state.trends.streamsDemographic,
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {
  fetchStreamsDaily,
  fetchStreamsCountry,
  fetchStreamsDemographic
})(Trends)